/**
 * Two derived labels on the Joining Tracker, kept out of the markup so they
 * can be tested and so the reasoning has somewhere to live.
 *
 * Both come from the client's spec for that screen. Neither stores anything
 * new: the month is a reading of a date the row already carries, and the
 * sourcing fee is a reading of two columns that were already on the row and
 * only visible inside the edit dialog.
 */
import type { FeeBasis } from '../types';

/**
 * The month a tracker row files under.
 *
 * **Derived from `submittedOn`, not from the joining date**, and that is worth
 * stating because it is the surprising half. The tracker's year/month filter
 * narrows on `Submission.submitted_on` (`tracker.py`, `apply_tracker_filters`)
 * with a comment saying what it costs: "a row submitted in February and
 * joined in March files under February, and the tracker's monthly counts will
 * not match the billing month."
 *
 * This column reads the same field the filter does, deliberately. A column
 * showing the joining month beside a filter narrowing on the submission month
 * would let somebody filter to September and get rows labelled August --
 * a screen disagreeing with its own controls, which is worse than a column
 * that means something narrower than the reader assumed.
 *
 * Whether the business means the joining month here is an open question, and
 * changing it is a one-line change *plus* the matching filter change. Doing
 * one without the other is the bug described above.
 *
 * Formatted short-month rather than numeric: "09/2026" and "2026-09" both
 * invite the day-month-year confusion this codebase already formats dates to
 * avoid.
 */
export function monthLabel(submittedOn: string | null | undefined): string | null {
  if (!submittedOn) return null;
  // Parsed off the ISO string rather than through Date, which would apply the
  // browser's timezone and can land a 1st-of-the-month row in the previous
  // one. The server sends a plain date; this reads it as one.
  const match = /^(\d{4})-(\d{2})/.exec(submittedOn);
  if (!match) return null;
  const [, year, month] = match;
  const index = Number(month) - 1;
  const names = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  if (index < 0 || index > 11) return null;
  return `${names[index]} ${year}`;
}

/**
 * How the fee was arrived at — the spec's "Sourcing Fee: Flat/%".
 *
 * Explains the agreed fee rather than producing it. `FeeBasis`' own docstring
 * is explicit that nothing multiplies CTC by the percentage to derive a fee:
 * what was agreed with the client is what is billed, and this only records how
 * the two of them got there.
 *
 * So a percentage basis with no percentage recorded still reads "% of CTC" —
 * the basis is the fact, and the number is a detail somebody may not have
 * captured. Returning null there would hide a recorded basis because a
 * secondary field was blank.
 */
export function sourcingFeeLabel(
  feeBasis: FeeBasis | null | undefined,
  feePercent: string | null | undefined,
): string | null {
  if (!feeBasis) return null;
  if (feeBasis === 'flat') return 'Flat';
  // Trailing zeros trimmed: the column holds Numeric(5,2) so 8.33 arrives as
  // "8.33" and 10 as "10.00", and "10.00% of CTC" is noise.
  const pct = feePercent ? String(Number(feePercent)) : null;
  return pct ? `${pct}% of CTC` : '% of CTC';
}
